<template>
  <div>
    <card
      :title="$t('pages.payment_method.location')"
      v-if="initialLoading || (!initialLoading && this.setupIsDone())"
      v-loading="initialLoading"
    >
      <el-row :gutter="25" class="mb-4">
        <el-col :xs="12" :sm="12" :lg="6">
          <label class="input-label mb-2">
            {{ $t("pages.payment_method.labels.id") }}
            <p>{{ this.location.locationId }}</p>
          </label>
        </el-col>
        <el-col v-if="this.location.name" :xs="12" :sm="12" :lg="6">
          <label class="input-label mb-2">
            {{ $t("pages.payment_method.labels.name") }}
            <p>{{ this.location.name }}</p>
          </label>
        </el-col>
        <el-col :xs="12" :sm="12" :lg="6">
          <label class="input-label mb-2">
            {{ $t("pages.payment_method.labels.address") }}
            <p>{{ location.address }}</p>
          </label>
        </el-col>
        <el-col :xs="12" :sm="12" :lg="6">
          <label class="input-label mb-2">
            {{ $t("pages.payment_method.labels.city") }}
            <p>{{ this.location.city }}</p>
          </label>
        </el-col>
        <el-col :xs="12" :sm="12" :lg="6">
          <label class="input-label mb-2">
            {{ $t("pages.payment_method.labels.country") }}
            <p>{{ this.location.country }}</p>
          </label>
        </el-col>
      </el-row>
    </card>
    <card
      :title="$t('pages.payment_method.location_not_found')"
      v-if="!initialLoading && !this.setupIsDone()"
      v-loading="initialLoading"
    >
      <p>{{ this.message }}</p>
    </card>
    <card 
      :title="$t('pages.payment_method.billing_details')" 
      v-if="!initialLoading && this.setupIsDone()"
      v-loading="paymentLoading"
    >
      <el-row :gutter="25">
        <el-col :xs="24" :sm="24" :lg="24">
          <label class="input-label mb-2">
            {{ $t("pages.payment_method.labels.email") + " *" }}
            <label class="optional-label mb-2">
              <el-input class="input" v-model="email" />
              <p
                class="error"
                v-if="
                  !emailIsValid(
                    email
                  )
                "
              >
                Invalid Email
              </p>
            </label>
          </label>
        </el-col>
      </el-row>
      <el-row :gutter="25">
        <el-col :xs="24" :sm="24" :lg="24">
          <label class="input-label mb-2">
            {{ $t("pages.payment_method.labels.fiscalNumber") }}
            <label class="optional-label mb-2">
              <el-input class="input" v-model="nif" />
              <p
                class="error"
                v-if="
                  !nifIsValid(
                    nif
                  )
                "
              >
                Invalid Fiscal Number
              </p>            
            </label>
          </label>
        </el-col>
      </el-row>
      <el-row :gutter="25">
        <el-col :xs="24" :sm="24" :lg="24">
          <el-checkbox v-model="acceptTerms" style="white-space: initial; display: inline-flex;">
            <span>
              {{ $t("pages.payment_method.labels.terms_of_service.beginning") }}
              <a :href="privacyPolicyLink">
                {{ $t("pages.payment_method.labels.terms_of_service.privacy_policy") }}
              </a>
              {{ $t("pages.payment_method.labels.terms_of_service.connector") }}
              <a :href="termsAndConditionsLink">
                {{ $t("pages.payment_method.labels.terms_of_service.terms_and_conditions") }}
              </a>
              {{ $t("pages.payment_method.labels.terms_of_service.ending") }}
            </span>
          </el-checkbox>
        </el-col>
      </el-row>
      <p style="margin-top: 20px;">({{ $t("pages.payment_method.labels.mandatory") }}) </p>
    </card>
    <div
      style="text-align: center;"
      v-if="!initialLoading"
      v-loading="paymentLoading"
    >
      <el-button class="button" @click="submit()" :round="true" :disabled="submitIsDisable()">
        {{ $t("pages.payment_method.continue").toUpperCase()  }}
      </el-button>
      <el-button class="button" @click="cancelSession()" :round="true">
        {{ $t("pages.payment_method.go_back").toUpperCase()  }}
      </el-button>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
import { getTariff } from "@/services/tariff";
const { mapActions, mapGetters, mapMutations } = createNamespacedHelpers("session");

export default {
  name: "PaymentMethod",
  props: {
    countryCode: String,
    partyId: String,
    locationId: String
  },
  data() {
    return {
      location: this.getLocation(),
      email: undefined,
      nif: undefined,
      tariff: undefined,
      initialLoading: undefined,
      paymentLoading: false,
      message: undefined,
      acceptTerms: false,
      termsAndConditionsLink: 'https://www.prio.pt/share/downloads/file357_pt.pdf',
      privacyPolicyLink: 'https://prio.pt/share/downloads/file366_pt.pdf'
    };
  },
  methods: {
    ...mapActions(["fetchLocationById", "createPayment"]),
    ...mapMutations(["setEvses"]),
    ...mapGetters(["getLocation", "getEvses", "getTariff", "getSessionStatus"]),
    submitIsDisable() {
      return !this.emailIsValid(this.email) || !this.nifIsValid(this.nif) || !this.acceptTerms || !this.setupIsDone();
    },
    localIsValid(str){
      //verify local part
      return str.charAt(0) !== "." &&
              str.charAt(str.length - 1) !== "." &&
              !str.includes("..") &&
              /^[a-zA-Z0-9!#$%&*+=?_.-]+$/.test(str);
    },
    domainIsValid(str){
      //verify domain
      // Uppercase and lowercase Latin letters A to Z and a to z;
      // Digits 0 to 9, provided that top-level domain names are not all-numeric;
      // Hyphen -, provided that it is not the first or last character.

      return /^[a-zA-Z0-9-._]+$/.test(str)
    },
    emailIsValid() {
      if (!this.email || this.email === "" || !this.email.includes("@")) {
        return false
      } else {
        const emailParts = this.email.split("@");
        return emailParts[0].length <= 64 &&
              emailParts[1].length <= 255 &&
              this.localIsValid(emailParts[0]) && 
              this.domainIsValid(emailParts[1]);
      }
    },
    nifIsValid() {
      if (!this.nif || this.nif === "") {return true}
      else {
        return /^[a-zA-Z0-9]{0,25}$/.test(this.nif)
      }
    },
    async submit() {
      try {
        this.paymentLoading = true
        let response = await this.createPayment({email: this.email, fiscalNumber: this.nif});
        this.paymentLoading = false
        this.$store.commit("session/setPaymentId", response.data.id);
        this.$store.commit("session/setPayment", response);
        this.$store.commit("session/changeStatusToCreated");
        this.$router.replace({
          name: "PaymentDetails",
          params: {
            reload: false,
            transaction: response
          }
        });
      } catch (error) {
        this.$message({
          type: "error",
          message: this.$t('pages.payment_method.error')
        });
        this.paymentLoading = false;
      }
    },
    async setupSession() {
      this.initialLoading = true
      let location
      try {

        this.$console("log", "FETCHING LOCATION BY ID");

        this.$console("log", this.$props.locationId);
        this.$console("log", this.$props.countryCode);
        this.$console("log", this.$props.partyId);

        location = await this.fetchLocationById({
          locationId: this.$props.locationId,
          countryCode: this.$props.countryCode,
          partyId: this.$props.partyId
        });

        this.$store.commit("session/setCountryCode", this.$props.countryCode);

        if (!location) {
          this.message = this.$t("pages.landing.location_not_available");
          // this.$message({
          //   type: "error",
          //   //message: this.$t("pages.landing.location_not_available")
          //   message: this.$t("pages.landing.location_not_available_qr_code")
          // });
          this.initialLoading = false;
        }        
      } catch (error) {
        this.message = this.$t("pages.landing.location_not_available");
        // this.$message({
        //   type: "error",
        //   message: this.$t("pages.landing.location_not_available")
        // });
        this.initialLoading = false;
      }

      if (!location.evses){
        this.message = this.$t("pages.landing.location_not_available");
        // this.$message({
        //   type: "error",
        //   // corrigir location does not exist
        //   message: this.$t("pages.landing.location_not_available")
        // });
        this.initialLoading = false;
      }
      else {
        let evse_array = [];

        for (let index = 0; index < location.evses.length; index++) {
          if (["AVAILABLE", "CHARGING", "UNKNOWN"].includes(location.evses[index].status)) {
            const tariff = await getTariff(this.$props.countryCode, this.$props.partyId, location.evses[index].connectors[0].tariff_ids)
            evse_array.push({
              uid: location.evses[index].uid,
              evse_id: location.evses[index].evse_id,
              status: location.evses[index].status,
              physical_reference: location.evses[index].physical_reference,
              connector_type: location.evses[index].connectors[0].standard,
              tariff: tariff
            });
          }
        }

        this.$console("log", "evse_array size : " +  evse_array.length)

        if (evse_array.length == 0) {
          this.message = this.$t("pages.landing.location_not_available");
          // this.$message({
          //   type: "error",
          //   message: this.$t("pages.landing.location_not_available")
          // });
          this.initialLoading = false;
        } else {
          this.$store.commit("session/setEvses", evse_array);
          this.initialLoading = false
        }
      }
    },
    setupIsDone() {
      const location = this.getLocation();
      const evses = this.getEvses();
      
      const conditionsMet =
        // Check country code
        (!location.countryCode || location.countryCode === "" || location.countryCode !== this.$props.countryCode) &&
        // Check party_id
        (!location.partyId || location.partyId === "" || location.partyId !== this.$props.partyId) &&
        // Check locationId
        (!location.locationId || location.locationId === "" || location.locationId !== this.$props.locationId) &&
        // Check evses
        (!evses || evses.length === 0)
        
      return !conditionsMet
    },
    cancelSession() {
      if (this.getSessionStatus() === "Active") {
        this.$message({
          type: "info",
          message: this.$t('pages.payment_method.active_session')
        });
      } else {
        this.$store.commit("session/clear");
        this.$message({
          type: "info",
          message: this.$t('pages.payment_method.cancelled_session')
        });
        this.$router.back();
      }
    }
  },
  async mounted() {
    if(!this.setupIsDone()){
      await this.setupSession();
    }
  }
};
</script>
<style lang="scss" scoped>
.optional-label{
  color: grey !important;
  font-weight: 300;
}
.example-showcase .el-loading-mask {
  z-index: 90;
}
.error {
  color: red !important;
}
</style>
